import CodeEditor from "../common/code-editor";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { developerAccessTokenState } from "../global-state";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { schemaScriptState } from "../common/schema-state";
import { SAND_CAT } from "../config";
import { useTheme } from "@mui/material/styles";

const SchemaBrowseView = () => {
  const theme = useTheme();
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );
  const [publishedSchemasLoading, setPublishedSchemasLoading] = useState(false);
  const [publishedSchemas, setPublishedSchemas] = useState([]);
  const [selectedPublishedSchema, setSelectedPublishedSchema] = useState("");

  const history = useHistory();
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftSchemas, setDraftSchemas] = useState([]);
  const [selectedDraftSchema, setSelectedDraftSchema] = useState("");
  const [code, setCode] = useState("");
  const [schemaScript, setSchemaScript] = useRecoilState(schemaScriptState);

  const [schemaRevisionsLoading, setSchemaRevisionsLoading] = useState(false);
  const [schemaRevisions, setSchemaRevisions] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [selectedSchemaRevision, setSelectedSchemaRevision] = useState("");

  const listPublishedSchemasIndex = async () => {
    try {
      setPublishedSchemasLoading(true);
      setSchemaRevisionsLoading(true);
      const response = await fetch(
        "https://api.mhomedev.com/v1/published-schemas",
        {
          method: "GET",
          headers: { Authorization: `Bearer ${developerAccessToken}` },
        },
      );
      const jsonData = await response.json();
      if (!response.ok) {
        throw new Error(jsonData);
      }
      setPublishedSchemas(jsonData);
      setPublishedSchemasLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSelectPublishedSchemaChange = event => {
    const selected = event.target.value;
    setSelectedPublishedSchema(selected);
    setSelectedSchemaRevision("");
    setCode("# Enter your schema");
    getSchemaRevisions(selected);
  };

  const listDraftSchemas = async () => {
    setDraftsLoading(true);
    const draftsUrl = "https://api.mhomedev.com/v1/draft-schemas";
    try {
      const draftsResponse = await fetch(draftsUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${developerAccessToken}` },
      });
      const drafts = await draftsResponse.json();
      if (!draftsResponse.ok) {
        throw new Error(drafts);
      }
      console.log(drafts);
      setDraftSchemas(drafts);
      setDraftsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSelectDraftSchemaChange = event => {
    let selected = null;
    for (let ds of draftSchemas) {
      if (ds["name"] === event.target.value) {
        selected = ds;
        break;
      }
    }
    if (selected) {
      setSelectedDraftSchema(selected["name"]);
      setCode(selected["rawData"]);
    } else {
      setSelectedDraftSchema("");
    }
  };

  useEffect(() => {
    // Fetch options data when the component mounts (optional)
    listPublishedSchemasIndex();
    listDraftSchemas();
  }, []);

  const getSchemaRevisions = async schemaName => {
    setSchemaRevisionsLoading(true);
    const queryParams = {
      name: schemaName,
      mode: "history",
    };
    const searchParams = new URLSearchParams(queryParams);
    const publishedBaseUrl = `${SAND_CAT}/schema/history`;
    const publishedUrl = `${publishedBaseUrl}?${searchParams.toString()}`;
    try {
      const publishedResponse = await fetch(publishedUrl, {
        method: "GET",
        headers: { DeveloperAuthorization: `Bearer ${developerAccessToken}` },
      });
      const published = await publishedResponse.json();
      if (!publishedResponse.ok) {
        throw new Error(published);
      }
      const revisionArray = [];
      for (let sr of published) {
        revisionArray.push({ value: sr.revision, display: `v${sr.revision}` });
      }
      setSchemaRevisions(published);
      setRevisions(revisionArray);
      setSchemaRevisionsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSelectSchemaRevisionChange = event => {
    let selected = null;
    for (let sr of schemaRevisions) {
      if (sr.revision === event.target.value) {
        selected = sr;
        break;
      }
    }
    setSelectedSchemaRevision(selected);
    setCode(selected["rawData"]);
  };

  const edit = () => {
    setSchemaScript(code);
    history.push(`/schema/edit`);
  };

  const selectStyle = loading => ({
    backgroundColor: loading ? "transparent" : theme.palette.background.input,
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select published schema</InputLabel>
                <Select
                  label="Select published schema"
                  value={selectedPublishedSchema}
                  onChange={handleSelectPublishedSchemaChange}
                  disabled={publishedSchemasLoading}
                  sx={selectStyle(publishedSchemasLoading)}
                >
                  <MenuItem value="" disabled>
                    {publishedSchemasLoading
                      ? "Loading published schemas..."
                      : "Select a schema"}
                  </MenuItem>
                  {publishedSchemas.map((option, index) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select schema revision</InputLabel>
                <Select
                  label="Select schema revision"
                  value={selectedSchemaRevision}
                  onChange={handleSelectSchemaRevisionChange}
                  disabled={schemaRevisionsLoading}
                  sx={selectStyle(schemaRevisionsLoading)}
                >
                  <MenuItem value="" disabled>
                    {publishedSchemasLoading
                      ? "Loading schema revisions..."
                      : "Select a revision"}
                  </MenuItem>
                  {revisions.map((option, index) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Select my draft</InputLabel>
            <Select
              label="Select my draft schema"
              value={selectedDraftSchema}
              onChange={handleSelectDraftSchemaChange}
              disabled={draftsLoading}
              sx={selectStyle(draftsLoading)}
            >
              <MenuItem value="" disabled>
                {draftsLoading ? "Loading draft schemas..." : "Select a draft"}
              </MenuItem>
              {draftSchemas.map((option, index) => (
                <MenuItem key={option["name"]} value={option["name"]}>
                  {option["name"]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button id="edit" variant="contained" color="primary" onClick={edit}>
            Edit
          </Button>
        </Grid>
        <Grid item>
          <CodeEditor
            value={code}
            editable={false}
            onChange={instance => {
              const code = instance.getValue();
              setCode(code);
            }}
            height={"800px"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchemaBrowseView;
